import { useEffect, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import { ProgressSpinner } from 'primereact/progressspinner';

interface DocxViewerProps {
  /** Direct file/blob to render (bypasses CORS issues) */
  file?: File | Blob | null;
  /** URL to fetch the document from (requires CORS) */
  url?: string | null;
  onLoadStart?: () => void;
  onLoadSuccess?: () => void;
  onLoadError?: (error: string) => void;
  className?: string;
  style?: React.CSSProperties;
}

export const DocxViewer = ({
  file,
  url,
  onLoadStart,
  onLoadSuccess,
  onLoadError,
  className,
  style,
}: DocxViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use refs for callbacks to avoid re-running effect when they change
  const onLoadStartRef = useRef(onLoadStart);
  const onLoadSuccessRef = useRef(onLoadSuccess);
  const onLoadErrorRef = useRef(onLoadError);

  // Keep refs up to date
  useEffect(() => {
    onLoadStartRef.current = onLoadStart;
    onLoadSuccessRef.current = onLoadSuccess;
    onLoadErrorRef.current = onLoadError;
  });

  useEffect(() => {
    if ((!file && !url) || !containerRef.current) return;

    const loadDocument = async () => {
      setLoading(true);
      setError(null);
      onLoadStartRef.current?.();

      try {
        let blob: Blob;

        if (file) {
          // Use the file directly (no CORS issues)
          blob = file;
        } else if (url) {
          // Fetch the document as a blob
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Failed to fetch document: ${response.status}`);
          }
          blob = await response.blob();
        } else {
          throw new Error('No file or URL provided');
        }

        // Clear previous content
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }

        // Render the DOCX
        await renderAsync(blob, containerRef.current!, undefined, {
          className: 'docx-preview',
          inWrapper: true,
          ignoreWidth: true, // Makes document responsive to container width
          ignoreHeight: true, // Allows natural height flow
          ignoreFonts: false,
          breakPages: false, // Disable page breaks for continuous flow
          useBase64URL: true,
        });

        setLoading(false);
        onLoadSuccessRef.current?.();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load document';
        setError(errorMessage);
        setLoading(false);
        onLoadErrorRef.current?.(errorMessage);
      }
    };

    loadDocument();
  }, [file, url]);

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        overflow: 'auto',
        backgroundColor: 'var(--bq-surface-muted)',
        ...style,
      }}
    >
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 10,
          }}
        >
          <div className="flex flex-column align-items-center gap-2">
            <ProgressSpinner style={{ width: '40px', height: '40px' }} />
            <span className="text-sm text-gray-400">Loading document...</span>
          </div>
        </div>
      )}

      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#ef4444',
            padding: '1rem',
            textAlign: 'center',
          }}
        >
          <div>
            <i
              className="pi pi-exclamation-triangle"
              style={{ fontSize: '2rem', marginBottom: '0.5rem' }}
            />
            <p>{error}</p>
          </div>
        </div>
      )}

      <div
        ref={containerRef}
        style={{
          minHeight: '100%',
          visibility: loading || error ? 'hidden' : 'visible',
        }}
      />
    </div>
  );
};
